import type { User } from "firebase/auth";
import { memberName } from "./memberNames";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { go } from "./hooks";
import { AREA_CODE_TEXT, areaCode, polygonText, salespeopleText, sameIds, sourceText, startIds, switchState, toggled } from "./salesAreas";
import { saveFailure } from "./save";
import { Skeleton } from "./Skeleton";

/** Rule Q-36: the salespeople of each area. A new lead in the area goes to one of them. */
export function SalesAreaSettings({ base, user }: { base: string; user: User }) {
  const [found, setFound] = useState<api.SalesAreas | "failed" | undefined>(undefined);
  const [editing, setEditing] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [newCode, setNewCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const first = useRef<HTMLInputElement>(null);
  const codeField = useRef<HTMLInputElement>(null);
  const table = useRef<HTMLDivElement>(null);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.salesAreas)
      .then(setFound, () => setFound("failed"));
  }, [user]);

  if (found === undefined) {
    return (
      <fieldset className="sales-areas" aria-busy="true">
        <legend>Sales areas</legend>
        <Skeleton shape="form" rows={13} phoneRows={27} label="Loading the sales areas" />
      </fieldset>
    );
  }
  if (found === "failed") {
    return (
      <fieldset className="sales-areas">
        <legend>Sales areas</legend>
        <p className="error">Cannot load the sales areas. Try again later.</p>
      </fieldset>
    );
  }
  const say = (ok: boolean, text: string) => {
    setMessage({ ok, text });
    requestAnimationFrame(() => status.current?.focus());
  };
  const open = (area: api.SalesArea) => {
    setEditing(area.id);
    setChosen(startIds(found.owners, area.salespeople));
    setMessage(null);
    requestAnimationFrame(() => first.current?.focus());
  };
  const close = (area: api.SalesArea) => {
    setEditing(null);
    requestAnimationFrame(() => table.current?.querySelector<HTMLButtonElement>(`[data-testid=sales-area-edit][data-area="${area.code}"]`)?.focus());
  };
  const save = async (area: api.SalesArea) => {
    setSaving(true);
    try {
      const next = await api.setAreaSalespeople(await user.getIdToken(), area.id, chosen);
      setFound(next);
      setEditing(null);
      const saved = next.areas.find((row) => row.id === area.id)?.salespeople ?? [];
      say(true, `Saved area ${area.code}: ${salespeopleText(saved)}.`);
    } catch (e) {
      say(false, saveFailure(e, "The salespeople").text);
    } finally {
      setSaving(false);
    }
  };
  const add = async () => {
    const code = areaCode(newCode);
    if (!code) {
      setCodeError(AREA_CODE_TEXT);
      requestAnimationFrame(() => codeField.current?.focus());
      return;
    }
    setCodeError(null);
    setSaving(true);
    try {
      setFound(await api.createArea(await user.getIdToken(), code));
      setNewCode("");
      say(true, `Added area ${code}.`);
    } catch (e) {
      say(false, saveFailure(e, "The area").text);
    } finally {
      setSaving(false);
    }
  };
  return (
    <fieldset className="sales-areas" data-testid="sales-area-settings" disabled={saving}>
      <legend>Sales areas</legend>
      <p className="hint">
        A new lead with no owner goes to the salesperson of the area that holds its pin. With more salespeople, the one with the fewest open leads gets it.
        A lead with no pin, or out of each area, stays in the inbox.
      </p>
      <p className="hint">
        Pickups: in the cutover, an area on the app counts only the app pickups of its accounts from its switch date. The sheet rows stay as the record.
        From that date, the import does not write the fields of its accounts. The app owns them, and a change in the sheet becomes an import issue. The
        switch is on the{" "}
        <a href={`${base}/cutover`} onClick={go} data-testid="sales-area-cutover">
          Cutover page
        </a>
        .
      </p>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Sales areas" ref={table}>
        <table className="list" data-testid="sales-area-table">
          <thead>
            <tr>
              <th scope="col">Area</th>
              <th scope="col">Region</th>
              <th scope="col">Week</th>
              <th scope="col">Polygon</th>
              <th scope="col">Pickups</th>
              <th scope="col">Salespeople</th>
              {found.can_edit && <th scope="col">Change</th>}
            </tr>
          </thead>
          <tbody>
            {found.areas.map((area) => {
              const editingRow = editing === area.id;
              return (
                <tr key={area.id} data-testid="sales-area-row" data-area={area.code}>
                  <th scope="row">
                    <span className="pin-swatch" style={{ background: area.colour }} aria-hidden="true" /> {area.code}
                  </th>
                  <td data-label="Region">{area.region}</td>
                  <td data-label="Week">{area.rotation_week}</td>
                  <td data-label="Polygon">{polygonText(area.has_polygon)}</td>
                  <td className="wrap" data-label="Pickups" data-testid="area-source" data-source={switchState(area)}>
                    <a href={`${base}/cutover`} onClick={go} aria-label={`${sourceText(area)}: open the Cutover page`}>
                      {sourceText(area)}
                    </a>
                  </td>
                  <td className="wrap people" data-label="Salespeople" data-testid="sales-area-people">
                    {editingRow ? (
                      <>
                        {found.owners.length ? (
                          <ul className="sales-checks" aria-label={`Salespeople of area ${area.code}`}>
                            {found.owners.map((person, index) => (
                              <li key={person.id}>
                                <label>
                                  <input
                                    type="checkbox"
                                    data-testid="sales-area-person"
                                    ref={index === 0 ? first : undefined}
                                    checked={chosen.includes(person.id)}
                                    onChange={() => setChosen(toggled(chosen, person.id))}
                                  />
                                  {memberName(person.email)}
                                </label>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No member can own a lead.</p>
                        )}
                        <p className="actions">
                          <button
                            type="button"
                            className="primary"
                            data-testid="sales-area-save"
                            disabled={sameIds(chosen, area.salespeople.map((person) => person.id))}
                            onClick={() => void save(area)}
                          >
                            {saving ? "Saving…" : "Save"}
                          </button>{" "}
                          <button type="button" data-testid="sales-area-cancel" onClick={() => close(area)}>
                            Cancel
                          </button>
                        </p>
                      </>
                    ) : (
                      salespeopleText(area.salespeople)
                    )}
                  </td>
                  {found.can_edit && (
                    <td data-label="">
                      {area.can_edit && !editingRow && (
                        <button
                          type="button"
                          className="quiet"
                          data-testid="sales-area-edit"
                          data-area={area.code}
                          aria-label={`Change the salespeople of area ${area.code}`}
                          disabled={editing !== null}
                          onClick={() => open(area)}
                        >
                          Change
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!found.areas.length && <p data-testid="sales-area-empty">No area yet. Add the first area below.</p>}
      {found.can_create && (
        <form
          className="actions"
          data-testid="area-add"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <label>
            Add an area{" "}
            <input
              ref={codeField}
              data-testid="area-add-code"
              value={newCode}
              placeholder="S3.04"
              autoCapitalize="characters"
              aria-invalid={codeError ? true : undefined}
              aria-describedby={codeError ? "area-add-error area-add-hint" : "area-add-hint"}
              onChange={(e) => {
                setNewCode(e.target.value);
                setCodeError(null);
              }}
            />
          </label>{" "}
          <button type="submit" data-testid="area-add-save" disabled={!newCode.trim()}>
            Add
          </button>
          {codeError && (
            <span id="area-add-error" className="error" role="alert" data-testid="area-add-error">
              {" "}
              {codeError}
            </span>
          )}
          <span id="area-add-hint" className="sub">
            {" "}
            The letter is the region and the digit after it is the rotation week. A source that has the same code uses this area.
          </span>
        </form>
      )}
      <p ref={status} tabIndex={-1} role="status" data-testid="sales-area-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </fieldset>
  );
}
